import { z } from 'zod';
import {
  budgetCategoryOutputSchema,
  currencyCodeSchema,
  moneyAmountSchema,
  nullableMoneyAmountSchema,
} from '../pricing';
import {
  dateFormatSchema,
  distanceUnitSchema,
  hourFormatSchema,
  itineraryChainSchema,
} from './itinerary';
import { productAttachmentSchema } from './products';

// Wire shapes for the customer-facing itinerary preview. Mirror
// `itinerary.ts` structure but strip every consultant-only field. Price
// jsonb is typed as `z.json()` — pricing surfaces are deferred until
// the new pricing module ships, but the value stays a concrete
// serializable JSON tree (server-fn boundaries reject `unknown`).

// Assigned Driver / Guide contact surfaced on a Transport / Guide subitem.
// Resolved from the item's `tour_operators` id array against `crm.tour_operator`
// and narrowed to the customer-relevant fields only. In CUSTOMER mode these are
// gated server-side: present only within 12h of the itinerary's start (so the
// phone number never reaches the wire earlier). Consultant mode always sets it.
export const previewTourContactSchema = z.object({
  type: z.enum(['Driver', 'Guide']),
  fullName: z.string(),
  phone: z.string().nullable(),
});

export type PreviewTourContact = z.infer<typeof previewTourContactSchema>;

// Strict allow-list projection of `itineraryItemBaseSchema`. Compared to the
// planner's item shape, this OMITS: privateNotes, notes (consultant-only),
// pricingType / pricingUnits / isGross (per-row breakdown is consultant
// territory), suppliers, isLine, isPricingHidden, isPreviewHidden,
// catalogId, operationStatusId, customerNotes (retired — never shown).
export const previewItemSchema = z.object({
  id: z.uuid(),
  productId: z.uuid().nullable(),
  parentId: z.uuid().nullable(),
  title: z.json(),
  description: z.json(),
  thumbnail: z.string().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  currency: z.string().nullable(),
  // Opaque jsonb — FE renders whatever the row stored. New pricing module
  // will define a concrete shape later.
  price: z.json(),
  travellers: z.json(),
  legs: z.json(),
  details: z.json(),
  tags: z.json(),
  facilities: z.json(),
  // Typed for the export pipeline. Storage is jsonb on `itinerary_item.attachments` —
  // legacy keeps every row loosely-typed and lets each consumer pick fields. Mirroring
  // `productAttachmentSchema` here gives the PDF/PPTX exporters a stable shape to filter
  // by `type === 'IMAGE'` and read `fileKey`/`url` without runtime guards everywhere.
  attachments: z.array(productAttachmentSchema).nullable(),
  videos: z.json(),
  // Consultant-internal notes copied verbatim into the customer-facing export
  // (legacy PDF + PPTX read `item.notes` for the "additional notes" block). Kept on
  // the preview projection so the export modules don't need a separate fetch.
  notes: z.string().nullable(),
  isOptional: z.boolean().nullable(),
  stars: z.string().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  optionalItems: z.json(),
  showPreviewOptionalPrice: z.boolean().nullable(),
  createdOn: z.string(),
  // Assigned Driver/Guide contacts (Transport/Guide subitems). `null` when none
  // are assigned OR — in customer mode — when the 12h-before-start gate hasn't
  // opened yet. The FE shows the contact when present, else a "assigned 12h in
  // advance" placeholder.
  tourOperators: z.array(previewTourContactSchema).nullable(),
});

export type PreviewItem = z.infer<typeof previewItemSchema>;

// Per-day public-holiday projection. Computed by
// `projectDayHolidays` (apps/itineraries/api) from the cached
// `itineraries.holidays` table, keyed by (countryName, year). `hidden`
// mirrors whether the day's date sits in the itinerary's
// `hiddenHolidays` array — the FE renders the badge for non-hidden
// entries and can offer a "show hidden" affordance from the flag.
// Customer-visible by design (matches legacy preview, which renders
// the holiday badge regardless of `?view=traveller`).
export const previewHolidaySchema = z.object({
  name: z.string(),
  date: z.string(),
  hidden: z.boolean(),
});

export type PreviewHoliday = z.infer<typeof previewHolidaySchema>;

export const previewDaySchema = z.object({
  index: z.number().int().nonnegative(),
  date: z.string().nullable(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  items: z.array(previewItemSchema),
  // Always present — `[]` when the itinerary has no country, no matching
  // cache row, or the day's date doesn't land on any cached holiday.
  holidays: z.array(previewHolidaySchema),
});

export type PreviewDay = z.infer<typeof previewDaySchema>;

// Resolved from `layout_template.settings` jsonb + uploadthing logo key.
export const previewAppearanceSchema = z.object({
  logoUrl: z.string().nullable(),
  primaryColor: z.string().nullable(),
});

export type PreviewAppearance = z.infer<typeof previewAppearanceSchema>;

// Minimal customer + consultant projections so the FE can render
// "From <consultant>" and "Prepared for <customer>" without exposing
// internal IDs / audit fields.
export const previewCustomerSchema = z.object({
  id: z.uuid().nullable(),
  // First/last names — populated when the itinerary's `contactId` points
  // at a `crm.contact` row. Null when the customer is a company (the
  // `v_customer` view UNIONs contacts + companies; companies surface only
  // a single `name` string).
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  // Single-string customer display name. Mirrors legacy
  // `Customer.name` (from `crm.v_customer.name` — concat of first/last
  // for a contact, or the company name when `contactId` is a company).
  // PDF/PPTX exports surface this directly in the logo bar.
  name: z.string().nullable(),
  // Customer's company name (when the customer is a contact; null for
  // direct company customers). Appended after the name in the legacy
  // logo bar (`"Josh Flatman - Faran Travel"`).
  companyName: z.string().nullable(),
  email: z.string().nullable(),
  // Customer's own CRM phone (`v_customer.phone` — office_phone for the
  // matched contact/company). Surfaced on the logo bar contact line next
  // to the email. Distinct from `PreviewItinerary.supportPhoneNumber`,
  // which is the org-level "Support 24/7" number rendered elsewhere.
  phone: z.string().nullable(),
});

export type PreviewCustomer = z.infer<typeof previewCustomerSchema>;

export const previewAccountSchema = z.object({
  id: z.uuid().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  // Consultant's email. Legacy PDF hero shows this under the account name
  // in the logo bar so the customer knows whom to reply to.
  email: z.string().nullable(),
});

export type PreviewAccount = z.infer<typeof previewAccountSchema>;

// Map-page marker emitted by `projectMapMarkers`. Discriminator `kind`
// distinguishes the seven legacy marker types (item / leg-start / leg-end /
// accommodation check-in / accommodation stay / accommodation check-out /
// rental pickup / rental drop-off). Coordinates are decimal degrees in
// WGS84. Title kept opaque — FE resolves localized text the same way it
// does for day items.
export const previewMarkerKindSchema = z.enum([
  'item',
  'leg-start',
  'leg-end',
  'accommodation-check-in',
  'accommodation-stay',
  'accommodation-check-out',
  'rental-pickup',
  'rental-drop-off',
  // Transfer items (Transportation + serviceType = Transfer) emit a
  // dedicated pickup + drop-off pair. The FE pairs them via the
  // `-destination` id-suffix convention to draw a 2-point route.
  'transfer-pickup',
  'transfer-drop-off',
]);

export type PreviewMarkerKind = z.infer<typeof previewMarkerKindSchema>;

// Per-day entry for the stays + optionals projections. Same shape used by
// both: stays may surface the same accommodation item twice (check-in day
// + check-out day) for multi-night stays — the FE distinguishes by
// comparing item.endTime against the day's date, or for undated days (no
// date) by treating the later occurrence as the check-out.
// Optionals surface each is_optional item once at the day its startTime
// resolves to.
export const previewDayEntrySchema = z.object({
  dayIndex: z.number().int().nonnegative(),
  item: previewItemSchema,
});

export type PreviewDayEntry = z.infer<typeof previewDayEntrySchema>;

// One "Alternative for these activities" row beneath an optional item. `item`
// is the base activity the optional can replace (resolved from the optional's
// `optionalItems` id array). `priceDifference` is `optionalTotal - baseTotal`,
// computed SERVER-SIDE from unmasked prices — so the customer payload exposes
// only the delta, never the optional's absolute (masked) price. Null when
// either side has no resolvable price.
export const previewOptionalAlternativeSchema = z.object({
  item: previewItemSchema,
  priceDifference: z.number().nullable(),
});

export type PreviewOptionalAlternative = z.infer<
  typeof previewOptionalAlternativeSchema
>;

// Optionals-route entry: a day-bucketed optional item plus the activities it
// can replace. Distinct from `previewDayEntrySchema` (used by stays, which has
// no alternatives). `alternatives` is `[]` when the optional links to nothing.
export const previewOptionalEntrySchema = previewDayEntrySchema.extend({
  alternatives: z.array(previewOptionalAlternativeSchema),
});

export type PreviewOptionalEntry = z.infer<typeof previewOptionalEntrySchema>;

// Customer-safe projection of the shared pricing module's BudgetOutput.
// Strips audit rows, redistribution metadata, and diagnostics — those are
// pricing internals that shouldn't leak to the customer payload. Keeps the
// shape legacy customer-mode budget breakdown exposed: per-category totals,
// grand total, target budget, remaining.
export const previewBudgetSchema = z.object({
  baseCurrency: currencyCodeSchema,
  categories: z.object({
    accommodation: budgetCategoryOutputSchema,
    activities: budgetCategoryOutputSchema,
    dining: budgetCategoryOutputSchema,
    logistic: budgetCategoryOutputSchema,
    other: budgetCategoryOutputSchema,
  }),
  total: moneyAmountSchema,
  // Target budget the consultant set on the active version (null = unset).
  budget: nullableMoneyAmountSchema,
  // budget - total when budget != null; null otherwise.
  remaining: nullableMoneyAmountSchema,
});

export type PreviewBudget = z.infer<typeof previewBudgetSchema>;

export const previewMarkerSchema = z.object({
  // Stable marker id: `${itemId}` for single-marker items, `${itemId}-${kind}`
  // for multi-marker items (e.g. flights, multi-night accommodations).
  id: z.string(),
  itemId: z.uuid(),
  parentId: z.uuid().nullable(),
  dayIndex: z.number().int().nonnegative(),
  kind: previewMarkerKindSchema,
  latitude: z.number(),
  longitude: z.number(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  title: z.unknown(),
  // UTC ISO. Populated on kinds the FE displays as a "starts at" time
  // (leg-start, accommodation-check-in, rental-pickup, item, and the
  // synthetic 06:00 stay-day marker). Null otherwise.
  startTime: z.string().nullable(),
  // UTC ISO. Populated on kinds the FE displays as a "ends at" time
  // (leg-end, accommodation-check-out, rental-drop-off). Null otherwise.
  endTime: z.string().nullable(),
  // Driver-services partition flags. Both false unless the row is an
  // unparented Transportation/DriverServices item with `serviceType`
  // matching Full / Half. Phase D routing uses these to elide the
  // synthetic transfer route segments.
  isFullDayTransfer: z.boolean(),
  isHalfDayTransfer: z.boolean(),
  // True for any contentCategory === 'Transportation' marker — drives the
  // map topbar's transport count.
  isTransportation: z.boolean(),
  // For top-level items: number of Transportation subitems whose
  // `parentId` points at this item. Non-Transfer transport subitems
  // (e.g. driver services) never become markers themselves but are
  // included in this count so the topbar matches legacy parity
  // (`PreviewMap/index.tsx:952-956` `+ filteredPosix.reduce(...
  // i.transportSubitemCount, 0)`). Always 0 for subitem markers.
  transportSubitemCount: z.number().int().nonnegative(),
  // First Image attachment URL on the item, used by the popup. Legacy
  // `getAttachmentUrl` (`app/preview/[id]/map/page.tsx:41-45`) prefers
  // the uploadthing fileKey (rendered as `https://utfs.io/f/${fileKey}`)
  // and falls back to a raw `url`. Null when the item has no Image
  // attachment — the FE renders a placeholder in that case.
  imageUrl: z.string().nullable(),
});

export type PreviewMarker = z.infer<typeof previewMarkerSchema>;

export const previewItinerarySchema = z.object({
  // The itinerary id, or `draft-<uuid>` for a Mini Planner draft's preview
  // (`draft-preview-id.ts`) — the route param every preview link re-uses.
  id: z.union([z.uuid(), z.string().regex(/^draft-[0-9a-f-]{36}$/i)]),
  itineraryRef: z.string().nullable(),
  title: z.string().nullable(),
  // Consultant-only. The id of the version that drives this preview. Used
  // by mutations that target the active version (e.g. `generateAiTitles`,
  // edit-day-title) without forcing a side-fetch of the protected
  // `itineraries.byId` route. `stripConsultantItinerary` deletes this
  // field for customer payloads.
  activeVersionId: z.uuid().nullable().optional(),
  languageCode: z.string().nullable(),
  // XT-158: per-itinerary format settings (end-user criteria). Nullable on
  // the wire so partial/legacy preview payloads still parse; the renderers
  // fall back to the documented defaults (24h / full / km) when null.
  hourFormat: hourFormatSchema.nullable(),
  dateFormat: dateFormatSchema.nullable(),
  distanceUnit: distanceUnitSchema.nullable(),
  displayCurrency: z.string().nullable(),
  currency: z.string().nullable(),
  // XT-126: EUR -> USD rate for the traveller's display-only currency
  // toggle, read off the global `organizations.exchange_rates` table. Null
  // whenever the toggle must not appear: consultant mode, a non-EUR base
  // currency, or no usable rate row. `.optional()` so a payload from an API
  // build that predates this field still parses. NEVER default this to 1 —
  // the toggle hides instead, so a EUR figure is never shown under a USD
  // symbol. Display only: nothing is repriced or persisted from it.
  usdDisplayRate: z.number().positive().nullable().optional(),
  undated: z.boolean().nullable(),
  startTime: z.string().nullable(),
  finishTime: z.string().nullable(),
  durationDays: z.number().int().nullable(),
  travellers: z.unknown(),
  // Per-traveller contact info from `itinerary_version.travellers_contact_info`
  // jsonb — legacy storage is `string[]` of `"name: email/phone"` rows.
  // Hero "Specially designed by ... for ..." reads `[0].split(':')[0]`
  // to pin the customer-facing recipient name (the first traveller).
  // Customer-visible by design (matches legacy preview).
  // `.optional()` so deployments where the API hasn't been rebuilt yet
  // don't fail zod validation on the missing field — readers should
  // tolerate `undefined` the same way they treat `null`.
  travellersContactInfo: z.array(z.string()).nullable().optional(),
  hiddenHolidays: z.unknown(),
  // Consultant-only free-text scratchpad on the itinerary row. Surfaced
  // in the consultant payload for the overview notes section. The
  // customer payload strips this in `stripConsultantItinerary` — see
  // `apps/preview/api/src/preview/strip.ts` (`note` is part of the
  // `CONSULTANT_ONLY_ITINERARY_KEYS` allow-list).
  note: z.string().nullable(),
  // Consultant-only internal commentary on the itinerary row. Surfaced in
  // the consultant payload only; stripped from the customer payload via
  // `stripConsultantItinerary` (`privateNotes` is in the
  // `CONSULTANT_ONLY_ITINERARY_KEYS` allow-list). `.optional()` so the
  // customer payload (field deleted) still satisfies the schema.
  privateNotes: z.string().nullable().optional(),
  customer: previewCustomerSchema.nullable(),
  account: previewAccountSchema.nullable(),
  appearance: previewAppearanceSchema.nullable(),
  // Version-level attachments from `itinerary_version.attachments` jsonb.
  // Still feeds the PDF/PPTX hero fallback for itineraries with no chosen
  // photo. Same typed shape as `previewItem.attachments`.
  attachments: z.array(productAttachmentSchema).nullable(),
  /**
   * The itinerary's hero photo as an uploadthing key — the SAME value the CMS
   * storefront card and the synced CMS row use, resolved server-side by
   * `pickItineraryHeroImageKey` so the exported cover can never disagree with
   * the card. Priority: the Photos-tab image flagged as thumbnail → the first
   * Photos-tab image → an item thumbnail (the second Activity/Attraction, then
   * Accommodation, then any) → null.
   *
   * Resolved here rather than in the browser on purpose: the eligibility rule
   * (`IS NOT TRUE` on three nullable flags, plus the logistics-title
   * exclusion) is subtle enough that a second client-side implementation is
   * how the export and the card would silently drift apart.
   */
  heroImageKey: z.string().nullable(),
  // Support phone number from the itinerary's organization row. Legacy navbar
  // surfaces this beside the "T&C" popover for late-stage itineraries (sold
  // status). Top-level here (not on `account`) — it's an org-level setting.
  supportPhoneNumber: z.string().nullable(),
  // Numeric sales stage of the itinerary's status (e.g. 7 = "Reservas OK").
  // The 24/7 support phone is shown only from "Reservas OK" onwards
  // (`salesStage >= 7`). Null when the status has no parseable stage.
  salesStage: z.number().int().nullable(),
  // True once the itinerary reaches the SOLD sales stage (`soldOn` set). Kept
  // for callers needing the payment-flow timestamp; the support phone now
  // gates on `salesStage`. PDF/PPTX exports embed the number unconditionally.
  isSold: z.boolean(),
  /**
   * XT-720 — the trip is readable but is being adjusted, so the Traveller
   * Preview shows an "Under review" strip on every screen. True only on the
   * traveller path, and only for a trip that entered customer approval and has
   * been Confirmed at least once: a trip still in its first negotiation round
   * is not visible at all, and a marketing itinerary was never under review.
   *
   * Optional because an API build that predates this field would otherwise fail
   * the FE's parse (same rationale as `usdDisplayRate` above). Absent reads as
   * "not under review", which is the safe default — it hides the strip rather
   * than showing it on a finished trip.
   */
  isUnderReview: z.boolean().optional(),
  /**
   * XT-816 — traveller view of a trip the agent portal sent for price review:
   * every price is withheld server-side and the shell says so. Absent reads
   * as false.
   */
  isPendingPriceReview: z.boolean().optional(),
  /**
   * XT-898 — the status a trip the agent sent from the Mini Planner is at, shown
   * as a badge on the hero: Draft → Under Review → Confirmed → Final. Null for
   * every other itinerary, and for a status with no traveller meaning
   * (cancelled, sample…). Absent reads as null.
   */
  tripStatus: z
    .enum(['draft', 'under_review', 'quotation', 'confirmed', 'final'])
    .nullable()
    .optional(),
  // T&C HTML resolved against the itinerary's `languageCode`. Falls back to the
  // first entry in `terms_and_conditions.description` jsonb, or null when the
  // version has no T&C row. Customer-safe content.
  termsAndConditionsHtml: z.string().nullable(),
  days: z.array(previewDaySchema),
  // Per-day AI badge flags + a top-level convenience boolean. Both derive
  // from the rich `day_titles` jsonb (`DayTitleDto[].aiGenerated`).
  dayTitlesAiGenerated: z.array(z.boolean()),
  hasAiGeneratedTitles: z.boolean(),
  // Itinerary-level toggle for the customer-preview budget breakdown
  // surface (legacy `?view=traveller` parity). Always present — defaults
  // to false in storage.
  showBudgetBreakdownInPreview: z.boolean(),
  // Phase 4 chapters: passthrough jsonb. FE narrows as needed.
  chapters: z.unknown(),
  // `isLine = true` rows surfaced separately for the Prices tab. Customer
  // mode receives them but the FE only shows them when relevant.
  priceLines: z.array(previewItemSchema),
  // Multi-marker projection for the /preview/[id]/map route. Only emitted
  // when `include.mapMarkers` is set; null otherwise so the wire payload
  // stays lean for callers that don't render the map.
  mapMarkers: z.array(previewMarkerSchema).nullable(),
  // Accommodation-only projection for the /preview/[id]/stays route.
  // Multi-day stays produce two entries (check-in day + check-out day).
  stays: z.array(previewDayEntrySchema).nullable(),
  // is_optional items for the /preview/[id]/optionals route. These items
  // are NOT included in `days[].items[]` — they belong to the optionals
  // surface only. Each entry also carries the activities it can replace
  // (`alternatives`) for the "Alternative for these activities" section.
  optionals: z.array(previewOptionalEntrySchema).nullable(),
  // Customer-mode budget breakdown. Emitted only when `viewMode === 'traveller'`,
  // `itinerary.showBudgetBreakdownInPreview === true`, and `include.budget`
  // is set. Otherwise null — matches legacy where the bar is hidden unless the
  // consultant explicitly enabled it AND the URL carries `?view=traveller`.
  budget: previewBudgetSchema.nullable(),
  // Regional video resolved to a CDN URL for the hero banner. Legacy parity:
  // `HeroBanner` renders a fullscreen autoplay muted looping video when this
  // is set. Customer-visible — NOT stripped in customer mode.
  heroVideoUrl: z.string().nullable(),
  // Itinerary chaining — prev/next neighbours (with titles) so the preview
  // renders the chain nav arrows. Customer-visible by design. Neighbours
  // that are soft-deleted / disabled / cross-org resolve to `null` (the
  // read path guards this). Both sides null when not chained.
  chain: itineraryChainSchema,
});

export type PreviewItinerary = z.infer<typeof previewItinerarySchema>;

export type PreviewTripStatus = NonNullable<PreviewItinerary['tripStatus']>;

export const previewByIdInput = z.object({
  id: z.uuid(),
  include: z
    .object({
      subitems: z.boolean().default(false),
      lineItems: z.boolean().default(false),
      hidden: z.boolean().default(false),
      mapMarkers: z.boolean().default(false),
      stays: z.boolean().default(false),
      optionals: z.boolean().default(false),
      budget: z.boolean().default(false),
    })
    .default({
      subitems: false,
      lineItems: false,
      hidden: false,
      mapMarkers: false,
      stays: false,
      optionals: false,
      budget: false,
    }),
});

export type PreviewByIdInput = z.infer<typeof previewByIdInput>;

// Customer mode rejects `include.hidden` — the URL flag isn't a privilege
// escalation hatch. `token` is required when a Customer Preview link was
// minted (TCCMS-OWN01-007); marketing itineraries omit it.
export const previewByIdCustomerInput = z.object({
  id: z.uuid(),
  token: z.string().min(1).optional(),
  include: z
    .object({
      subitems: z.boolean().default(false),
      lineItems: z.boolean().default(false),
      mapMarkers: z.boolean().default(false),
      stays: z.boolean().default(false),
      optionals: z.boolean().default(false),
      budget: z.boolean().default(false),
    })
    .default({
      subitems: false,
      lineItems: false,
      mapMarkers: false,
      stays: false,
      optionals: false,
      budget: false,
    }),
});

export type PreviewByIdCustomerInput = z.infer<typeof previewByIdCustomerInput>;

// Traveller Preview of a Mini Planner draft (`accounts.planner_draft` on the
// CMS database) before it is sent: public by its unguessable id, like the
// traveller link. A draft has no line items, budget or hidden items to ask for.
export const previewDraftTravellerInput = z.object({
  draftId: z.uuid(),
  include: z
    .object({
      subitems: z.boolean().default(false),
      mapMarkers: z.boolean().default(false),
      stays: z.boolean().default(false),
      optionals: z.boolean().default(false),
    })
    .default({
      subitems: false,
      mapMarkers: false,
      stays: false,
      optionals: false,
    }),
});

export type PreviewDraftTravellerInput = z.infer<
  typeof previewDraftTravellerInput
>;

// Records one customer-preview view (the "Viewed Times Logs" source). Fired
// explicitly by the customer preview page once Clerk auth has resolved to a
// genuine customer — NOT a side effect of the read, so an agent's preview
// (or the auth-init transient) never inflates the count.
export const recordPreviewViewInput = z.object({
  id: z.uuid(),
  token: z.string().min(1).optional(),
});

export type RecordPreviewViewInput = z.infer<typeof recordPreviewViewInput>;

// Customer selects an optional alternative on the /optionals page → create a
// change-request task for the itinerary's consultant. Public input: only ids
// (the server resolves names, day, and the owning account — the client can't
// dictate task copy or who it's assigned to). `baseItemId` is the activity the
// optional replaces (must appear in the optional's `optionalItems`).
export const createOptionalChangeRequestInput = z.object({
  itineraryId: z.uuid(),
  optionalItemId: z.uuid(),
  baseItemId: z.uuid(),
  // Resolves the item titles in the task copy; falls back to the itinerary's
  // own language when omitted.
  languageCode: z.string().nullable().optional(),
  token: z.string().min(1).optional(),
});

export type CreateOptionalChangeRequestInput = z.infer<
  typeof createOptionalChangeRequestInput
>;

// Legacy `/pv/{shortId}` alias resolution. `shortItineraryId` is the 8-char
// uuid prefix (legacy short link) or a full uuid; `shortItemId` likewise.
export const previewResolveShortInput = z.object({
  shortItineraryId: z.string().min(1).max(36),
  shortItemId: z.string().min(1).max(36).optional(),
  token: z.string().min(1).optional(),
});
export type PreviewResolveShortInput = z.infer<typeof previewResolveShortInput>;

export const previewResolveShortResult = z.object({
  itineraryId: z.uuid(),
  itemId: z.uuid().nullable(),
});
export type PreviewResolveShortResult = z.infer<
  typeof previewResolveShortResult
>;
